import type { RealtimeChannel } from "ably";
import { useChannel as useAblyReactChannel } from "ably/react";
import { useCallback } from "react";
import type { SafePublishOptions } from "../core/publish";
import { safePublish as publishSafely, publishWithRetry } from "../core/publish";
import type { TypedChannel } from "../core/types";
import { useAblyConfig } from "./AblyProvider";

export function useChannel<TMessages extends {} = Record<string, unknown>>(
	channelName: string,
	onMessage?: (message: unknown) => void,
) {
	const { logger } = useAblyConfig();

	const { channel } = useAblyReactChannel(
		{
			channelName,
			onConnectionError: (error) => {
				logger.warning("INFO", "Ably channel connection error", {
					channelName,
					error: error?.message,
					code: error?.code,
				});
			},
		},
		onMessage,
	);

	const publish = useCallback(
		async <K extends keyof TMessages & string>(name: K, data: TMessages[K]): Promise<void> => {
			if (!channel) {
				throw new Error(`Channel not available - cannot send message: ${name}`);
			}

			const rawChannel = channel as unknown as RealtimeChannel;
			await publishWithRetry(rawChannel, { name, data });
		},
		[channel],
	);

	const safePublish = useCallback(
		async <K extends keyof TMessages & string>(
			name: K,
			data: TMessages[K],
			options: Omit<SafePublishOptions, "channel" | "channelName"> = {},
		): Promise<void> => {
			await publishSafely(
				{ name, data },
				{
					...options,
					channel: (channel as unknown as RealtimeChannel | null) ?? null,
					channelName,
				},
			);
		},
		[channel, channelName],
	);

	return {
		channel: channel as unknown as TypedChannel<TMessages> | null,
		channelName,
		publish,
		safePublish,
	};
}
